import { Select as BaseSelect } from '@base-ui/react/select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface SelectProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly SelectOption<T>[];
  /** Подпись для скринридера, если рядом нет видимой метки. */
  'aria-label'?: string;
  disabled?: boolean;
  className?: string;
}

/** Выбор одного значения из короткого списка (язык, роль участника, тип карточки). */
export function Select<T extends string>({
  value,
  onValueChange,
  options,
  disabled,
  className,
  ...aria
}: SelectProps<T>) {
  return (
    <BaseSelect.Root
      value={value}
      onValueChange={(next) => next !== null && onValueChange(next)}
      items={options}
      {...(disabled !== undefined ? { disabled } : {})}
    >
      <BaseSelect.Trigger
        {...aria}
        className={cn(
          'inline-flex h-9 min-w-36 items-center justify-between gap-2 rounded-sm border border-border bg-input px-3 text-sm outline-none hover:border-border-hover focus-visible:border-primary focus-visible:shadow-focus data-[disabled]:cursor-not-allowed data-[disabled]:text-hint',
          className,
        )}
      >
        <BaseSelect.Value />
        <BaseSelect.Icon className="flex text-muted-foreground">
          <ChevronDown className="size-4" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4} alignItemWithTrigger={false} className="z-50">
          <BaseSelect.Popup className="max-h-[var(--available-height)] min-w-[var(--anchor-width)] overflow-y-auto rounded-lg border border-border bg-card p-1 text-sm shadow-popover outline-none transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0">
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                className="flex h-8 cursor-default items-center gap-2 rounded-sm pr-2 pl-8 outline-none select-none data-[disabled]:text-hint data-[highlighted]:bg-tint"
              >
                <BaseSelect.ItemIndicator className="-ml-6 flex w-4">
                  <Check className="size-4" />
                </BaseSelect.ItemIndicator>
                <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
